"use client"

import {
    ColumnDef,
    ColumnFiltersState,
    SortingState,
    VisibilityState,
    flexRender,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from "@tanstack/react-table";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"

import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table"
import { Checkbox } from "@/components/ui/checkbox";
import { MoreVerticalIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import Create from "./Create"
import react from "react";
import { deleteVehicleClass, getVehicleClasses } from "../services/vehicle_service";
import { getCurrencySymbol } from "@/lib/utils";
import Edit from "./Edit";
import { toast } from "sonner";
import Delete from "../shared/Delete";
import { useUserContext } from "@/context/UserContext";

const VehicleClassList = () => {
    const [data, setData] = react.useState<any[]>([]);
    const [currency, setCurrency] = react.useState<string>('');
    const [rowSelection, setRowSelection] = react.useState({});
    const [sorting, setSorting] = react.useState<SortingState>([]);
    const [columnFilters, setColumnFilters] = react.useState<ColumnFiltersState>([]);
    const [columnVisibility, setColumnVisibility] = react.useState<VisibilityState>({});
    const { decodedToken } = useUserContext();

    const columns: ColumnDef<any>[] = [
        {
            id: "select",
            header: ({ table }) => (
                <Checkbox
                    checked={
                        table.getIsAllPageRowsSelected() ||
                        (table.getIsSomePageRowsSelected() && "indeterminate")
                    }
                    onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
                    aria-label="Select all"
                />
            ),
            cell: ({ row }) => (
                <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value) => row.toggleSelected(!!value)}
                    aria-label="Select row"
                />
            ),
            enableSorting: false,
            enableHiding: false,
        },
        {
            accessorKey: "name",
            header: "Category",
            cell: ({ row }: any) => (
                <div className="capitalize">{row.getValue("name")}</div>
            ),
        },
        {
            accessorKey: "description",
            header: "Description",
            cell: ({ row }: any) => (
                <div className="capitalize">{row.getValue("description")}</div>
            ),
        },
        {
            accessorKey: "base_price",
            header: `Base Price (${currency})`,
            cell: ({ row }) => (
                <div className="capitalize">{row.getValue("base_price")}</div>
            ),
        },
        {
            id: "actions"
        }
    ]


    const loadData = async () => setData(await getVehicleClasses());

    react.useEffect(() => {
        const unsub = async () => {
            if (!decodedToken?.user.id) return;

            const currency = getCurrencySymbol(decodedToken?.permissions?.currency);
            setCurrency(currency);

            await loadData();
        }
        unsub();
    }, [decodedToken?.user.id]);

    const confirmDelete = async (id: number) => {
        try {
            await deleteVehicleClass(id);
            toast.success("Vehicle class deleted successfully");
            await loadData();
        }
        catch (error: any) {
            console.error("Error deleting vehicle class:", error);
            toast.error(error.response?.data?.message || "Failed to delete vehicle class");
        }
    }

    const table = useReactTable({
        // @ts-ignore
        data,
        columns,
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: getCoreRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        onColumnVisibilityChange: setColumnVisibility,
        onRowSelectionChange: setRowSelection,
        state: {
            sorting,
            columnFilters,
            columnVisibility,
            rowSelection,
        },
    })

    return (
        <div className="w-full p-4">
            <div className="flex items-center justify-between py-4">
                <div className="flex-1">
                    <Input
                        placeholder="Filter categories..."
                        value={(table.getColumn("name")?.getFilterValue() as string) ?? ""}
                        onChange={(event) =>
                            table.getColumn("name")?.setFilterValue(event.target.value)
                        }
                        className="max-w-xs"
                    />
                </div>
                <div className="flex flex-row">
                    <Create currency={currency} title="Add Category" description="Create new vehicle category" onSaved={loadData} />
                </div>
            </div>

            <div className="rounded-md border">
                <Table>
                    <TableHeader className="bg-muted sticky top-0 z-10">
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => {
                                    return (
                                        <TableHead key={header.id}>
                                            {header.isPlaceholder
                                                ? null
                                                : flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                        </TableHead>
                                    )
                                })}
                            </TableRow>
                        ))}
                    </TableHeader>
                    <TableBody>
                        {table.getRowModel().rows?.length ? (
                            table.getRowModel().rows.map((row) => (
                                <TableRow
                                    key={row.id}
                                    data-state={row.getIsSelected() && "selected"}>
                                    {row.getVisibleCells().map((cell) => (
                                        cell.column.id === "actions" ? (
                                            <TableCell key={cell.id}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button
                                                            variant="ghost"
                                                            className="flex size-8 text-muted-foreground data-[state=open]:bg-muted"
                                                            size="icon">
                                                            <MoreVerticalIcon />
                                                            <span className="sr-only">Open menu</span>
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="w-32">
                                                        <Edit currency={currency} data={row.original} id={row.original.id} title="Edit" description="Update vehicle category" onSaved={loadData} />
                                                        <DropdownMenuSeparator />
                                                        <Delete id={row.original.id} onConfirmDelete={confirmDelete} />
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>) : (

                                            <TableCell key={cell.id}>
                                                {flexRender(
                                                    cell.column.columnDef.cell,
                                                    cell.getContext()
                                                )}
                                            </TableCell>))
                                    )}
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={columns.length}
                                    className="h-24 text-center"
                                >
                                    No results.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </div>
            <div className="flex items-center justify-end space-x-2 py-4">
                <div className="flex-1 text-sm text-muted-foreground">
                    {table.getFilteredSelectedRowModel().rows.length} of{" "}
                    {table.getFilteredRowModel().rows.length} row(s) selected.
                </div>
                <div className="space-x-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        Previous
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        Next
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default VehicleClassList;